/**
 * Revenue Pipeline Chart Component
 * Phase 6 Batch 3 - EOS Add-on Reports
 *
 * Forward-looking cash flow forecast showing scheduled work and proposals
 */
import { useRevenuePipeline, type RevenuePipelineData } from '@/hooks/useDashboardReports';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { AlertCircle, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function RevenuePipelineChart({ months = 6 }: { months?: number }) {
  const { data, isLoading, error } = useRevenuePipeline(months);
  const navigate = useNavigate();

  // MED-05: Navigate to proposals with age filter
  const handleProposalCardClick = (ageCategory: 'active' | 'aging' | 'stale') => {
    navigate(`/projects?workflow_stage=proposal_phase&proposal_age=${ageCategory}`);
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load revenue pipeline data
      </div>
    );
  }

  const pipelineData = data as { success: boolean } & RevenuePipelineData;
  const scheduledWork = pipelineData?.scheduled_work || [];
  const proposals = pipelineData?.proposals;
  const summary = pipelineData?.summary;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyShort = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${Math.round(value / 1000)}k`;
    }
    return `$${value}`;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="revenue-pipeline-chart">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp size={20} />
          Revenue Pipeline (Next {months} Months)
        </h2>
      </div>

      {/* Scheduled Work Chart */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Scheduled Work by Month</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={scheduledWork} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="0" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="month_label"
              tick={{ fontSize: 12, fill: '#6b7280' }}
              angle={-45}
              textAnchor="end"
              height={80}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
            />
            <Tooltip
              formatter={(value: number) => formatCurrency(value)}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
            />
            <Bar dataKey="scheduled_amount" name="Scheduled" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Proposals Breakdown - MED-05: Clickable cards */}
      {proposals && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Proposal Pipeline</h3>
          <div className="grid grid-cols-3 gap-4">
            <div
              className="bg-green-50 border border-green-200 rounded-lg p-4 cursor-pointer hover:bg-green-100 transition-colors"
              onClick={() => handleProposalCardClick('active')}
              data-testid="proposal-card-active"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleProposalCardClick('active')}
            >
              <div className="text-sm text-gray-600 mb-1">Active (&lt; 30 days)</div>
              <div className="text-xl font-bold text-emerald-700">{formatCurrency(proposals.active.total_value)}</div>
              <div className="text-xs text-gray-500 mt-1">
                {formatCurrencyShort(proposals.active.regular_value || 0)} regular · {formatCurrencyShort(proposals.active.optional_value || 0)} optional
              </div>
              <div className="text-xs text-gray-500">{proposals.active.count} proposals</div>
            </div>
            <div
              className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 cursor-pointer hover:bg-yellow-100 transition-colors"
              onClick={() => handleProposalCardClick('aging')}
              data-testid="proposal-card-aging"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleProposalCardClick('aging')}
            >
              <div className="text-sm text-gray-600 mb-1">Aging (30-60 days)</div>
              <div className="text-xl font-bold text-yellow-700">{formatCurrency(proposals.aging.total_value)}</div>
              <div className="text-xs text-gray-500 mt-1">
                {formatCurrencyShort(proposals.aging.regular_value || 0)} regular · {formatCurrencyShort(proposals.aging.optional_value || 0)} optional
              </div>
              <div className="text-xs text-gray-500">{proposals.aging.count} proposals</div>
            </div>
            <div
              className="bg-red-50 border border-red-200 rounded-lg p-4 cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => handleProposalCardClick('stale')}
              data-testid="proposal-card-stale"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleProposalCardClick('stale')}
            >
              <div className="text-sm text-gray-600 mb-1">Stale (&gt; 60 days)</div>
              <div className="text-xl font-bold text-red-700">{formatCurrency(proposals.stale.total_value)}</div>
              <div className="text-xs text-gray-500 mt-1">
                {formatCurrencyShort(proposals.stale.regular_value || 0)} regular · {formatCurrencyShort(proposals.stale.optional_value || 0)} optional
              </div>
              <div className="text-xs text-gray-500">{proposals.stale.count} proposals</div>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
          <div>
            <span className="text-gray-600 text-sm">Scheduled Work:</span>
            <div className="font-semibold text-lg text-slate-600">
              {formatCurrency(summary.total_scheduled)}
            </div>
          </div>
          <div>
            <span className="text-gray-600 text-sm">Total Proposals:</span>
            <div className="font-semibold text-lg text-sky-600">
              {formatCurrency(summary.total_proposals)}
            </div>
          </div>
          <div>
            <span className="text-gray-600 text-sm">Total Pipeline:</span>
            <div className="font-semibold text-lg text-gray-900">
              {formatCurrency(summary.total_pipeline)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
